// dashboard-rediseno (4.9, firma 7): LA PREPARACION DE CADA TARJETA DEL PANEL.
//
// ARCHIVO EN ASCII A PROPOSITO (regla de la casa para los `.js` nuevos).
//
// ES EL MISMO NUMERO QUE ANALITICAS, NO SE CALCULA NADA NUEVO:
//   - con red, `GET /panel/preparacion` (el servidor llama a la MISMA `get_overview` de Analiticas y
//     devuelve solo `{documentos: {id: score}, publicaciones: {id: score}}`, con entrada solo donde
//     hay dato). No se llama a `/analytics/overview`: gasta el cubo de arrancar sesiones de estudio;
//   - en la app sin red, la MISMA lectura local que usa Analiticas (`fetchAnalyticsOverview` en
//     `pages/Analytics.js`: `analyticsRunsLocally()` con dictamen de sin senal, o la caida por forma
//     de red, contra `warehouse/screenSources.analyticsOverviewFromWarehouse(userId, isGuest)`); ahi una
//     card vive como el documento `pub:<publicacion_id>`.
//
// MEMORIA POR USUARIO, 60 s: dentro de esa ventana no se vuelve a pedir. Se invalida (la siguiente
// lectura va a la red, y mientras tanto se pinta lo ultimo que llego) al borrar (el Panel llama a
// `invalidarPreparacion`), al renombrar (lo llama `renombrarDocumento.js`) y al salir de una sesion de
// estudio. Esa ultima es la senal que `App.js` ya usa (salir de `/study/exam`, `/study/flashcards` o
// `/study/adaptive`), enganchada aqui: el Panel no esta montado mientras se estudia, asi que al
// desmontarse mira a donde se fue, y ademas oye el aviso de sesion cerrada de `screenEvents.js` (el
// mismo que le tira a Analiticas su copia).
//
// SI FALLA O CONTESTA 429: en silencio (ni aviso ni error) y con el ultimo valor que llego en esta
// sesion. Sin dato, objetos vacios: la tarjeta no pinta nada.
//
// `lib/api` se pide al llamar y no arriba (la mina de `lib/api` -> `src/i18n` en las suites que
// montan el chasis sin doblarlo).
import { useEffect, useRef, useState } from 'react';
import { IS_APP_BUILD } from './buildTarget';
import { analyticsRunsLocally } from './brain/cutover';
import { isExplicitlyOffline } from './offline/networkVerdict';
import { SESSION_COMMITTED_EVENT } from './screenEvents';
import { useRaizActiva } from './pager/activacion';

export const MEMORIA_MS = 60_000;

/** Las pantallas de estudio de `App.js` (la misma lista). */
const RUTAS_DE_ESTUDIO = ['/study/exam', '/study/flashcards', '/study/adaptive'];
const PREFIJO_CARD = 'pub:';
const VERDE = '#4ADE80';
const NARANJA = '#F27A2E';

function vacio() {
  return { documentos: {}, publicaciones: {} };
}

function apiDeLaCasa() {
  return require('./api');
}

// { userId, valor, at } del ultimo dato que llego; `at` 0 = invalidado.
let memoria = null;
// { userId, promesa } de la peticion en vuelo (dos montajes a la vez piden una vez).
let enVuelo = null;

/** La siguiente lectura va a la red. Lo ultimo que llego se sigue pintando mientras tanto. */
export function invalidarPreparacion() {
  if (memoria) memoria = { ...memoria, at: 0 };
  enVuelo = null;
}

if (typeof window !== 'undefined' && window.addEventListener) {
  window.addEventListener(SESSION_COMMITTED_EVENT, invalidarPreparacion);
}

/** Solo para pruebas: deja el modulo como recien cargado. */
export function __reiniciarPreparacionParaPruebas() {
  memoria = null;
  enVuelo = null;
}

/** El texto que pinta Analiticas para ese valor ("{score}%"), o null sin dato. */
export function textoDePreparacion(valor) {
  if (typeof valor !== 'number' || !Number.isFinite(valor)) return null;
  return `${valor}%`;
}

export function colorDePreparacion(valor) {
  return valor >= 85 ? VERDE : NARANJA;
}

function soloNumeros(objeto) {
  const salida = {};
  if (!objeto || typeof objeto !== 'object' || Array.isArray(objeto)) return salida;
  Object.keys(objeto).forEach((id) => {
    const v = objeto[id];
    if (typeof v === 'number' && Number.isFinite(v)) salida[id] = v;
  });
  return salida;
}

function deLaRuta(datos) {
  return {
    documentos: soloNumeros(datos && datos.documentos),
    publicaciones: soloNumeros(datos && datos.publicaciones),
  };
}

// La lectura local devuelve la forma de `/analytics/overview`: una entrada por documento, y la de
// una card con el id `pub:<publicacion_id>`.
function deLaBodega(resumen) {
  const salida = vacio();
  const tarjetas = resumen && Array.isArray(resumen.doc_cards) ? resumen.doc_cards : [];
  for (const c of tarjetas) {
    const lectura = c && c.readiness;
    if (!lectura || lectura.has_data !== true) continue;
    if (typeof lectura.score !== 'number' || !Number.isFinite(lectura.score)) continue;
    if (typeof c.id !== 'string' || !c.id) continue;
    if (c.id.startsWith(PREFIJO_CARD)) {
      const pub = c.id.slice(PREFIJO_CARD.length);
      if (pub && !pub.includes(':')) salida.publicaciones[pub] = lectura.score;
    } else {
      salida.documentos[c.id] = lectura.score;
    }
  }
  return salida;
}

async function leerLaBodega(user) {
  const sources = await import('./warehouse/screenSources');
  const resumen = await sources.analyticsOverviewFromWarehouse(user.id, user.is_guest);
  if (!resumen) throw new Error('sin bodega');
  return deLaBodega(resumen);
}

async function pedir(user) {
  if (analyticsRunsLocally() && isExplicitlyOffline()) {
    try {
      return await leerLaBodega(user);
    } catch (e) { /* sin bodega utilizable: el camino de abajo */ }
  }
  if (IS_APP_BUILD && isExplicitlyOffline()) throw new Error('sin red');
  const casa = apiDeLaCasa();
  try {
    const res = await casa.default.get('/panel/preparacion');
    return deLaRuta(res && res.data);
  } catch (err) {
    if (analyticsRunsLocally() && casa.isNetworkShapedError(err)) return leerLaBodega(user);
    throw err;
  }
}

/** Lo ultimo que llego para ese usuario, o vacio. */
function loUltimo(userId) {
  return memoria && memoria.userId === userId ? memoria.valor : vacio();
}

/** La Preparacion del usuario: de la memoria si tiene menos de 60 s, si no de la red. Nunca lanza. */
export async function leerPreparacion(user) {
  const userId = user && user.id;
  if (!userId) return vacio();
  if (memoria && memoria.userId === userId && memoria.at > 0 && Date.now() - memoria.at < MEMORIA_MS) {
    return memoria.valor;
  }
  if (!enVuelo || enVuelo.userId !== userId) {
    const promesa = pedir(user).then((valor) => {
      if (enVuelo && enVuelo.promesa === promesa) {
        memoria = { userId, valor, at: Date.now() };
      }
      return valor;
    });
    enVuelo = { userId, promesa };
    promesa.finally(() => {
      if (enVuelo && enVuelo.promesa === promesa) enVuelo = null;
    }).catch(() => {});
  }
  try {
    return await enVuelo.promesa;
  } catch (e) {
    return loUltimo(userId);
  }
}

function salidaAEstudiar() {
  const ruta = typeof window !== 'undefined' && window.location ? window.location.pathname || '' : '';
  return RUTAS_DE_ESTUDIO.some((p) => ruta.startsWith(p));
}

/**
 * `{documentos: {id: valor}, publicaciones: {id: valor}}` para el Panel. Objetos vacios mientras no
 * hay dato. Pide solo con la raiz activa (el contrato del pager).
 */
export function usePreparacionDelPanel(user) {
  const activa = useRaizActiva();
  const userId = user && user.id;
  const userRef = useRef(user);
  userRef.current = user;
  const [valor, setValor] = useState(() => loUltimo(userId));

  useEffect(() => {
    let vivo = true;
    if (!userId) {
      setValor(vacio());
      return undefined;
    }
    setValor(loUltimo(userId));
    if (activa) {
      leerPreparacion(userRef.current).then((v) => {
        if (vivo) setValor(v);
      });
    }
    return () => {
      vivo = false;
      // Si el Panel se fue a una pantalla de estudio, al volver la Preparacion ya cambio.
      if (salidaAEstudiar()) invalidarPreparacion();
    };
  }, [userId, activa]);

  return valor;
}
